// src/pages/TradingData/StrategyGenerator.jsx
// Strategy Generator — one-click strategy discovery portal
// Pick entry/exit/companions with per-signal context → discover high win-rate strategies → download Excel

import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, Stack, Paper, Chip, IconButton,
  alpha, Snackbar, Alert, Tooltip, LinearProgress, Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  FilterAlt as FilterAltIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';

import { MONO } from './IndicatorConfigs';
import { Pill } from './SharedComponents';
import SignalPickerModal from './SignalPickerModal';

const DISCOVER_URL = '/strategies/api/signals/discover-strategies/';
const FILTER_COLOR = '#f59e0b';
const SLIPPAGE = 0.05; // % — matches the Trading Data page's slippage assumption

// Curated entry/exit pairs and default companions for AUTO mode
// (Runs client-side by calling the existing discover endpoint once per entry.)
const AUTO_ENTRIES = {
  LONG: [
    ['LUX_LONG', 'LUX_SHORT'],
    ['COBRA_LONG', 'COBRA_SHORT'],
    ['VGB_LONG', 'VGB_SHORT'],
    ['SMART_BAND_15', 'SMART_BAND_15_SHORT'],
    ['SMART_TRAIL_2', 'SMART_TRAIL_2_SHORT'],
    ['ALGO_PRO_LONG', 'ALGO_PRO_SHORT'],
    ['TARGET_TREND_LONG', 'TARGET_TREND_SHORT'],
  ],
  SHORT: [
    ['LUX_SHORT', 'LUX_LONG'],
    ['COBRA_SHORT', 'COBRA_LONG'],
    ['VGB_SHORT', 'VGB_LONG'],
    ['SMART_BAND_15_SHORT', 'SMART_BAND_15'],
    ['SMART_TRAIL_2_SHORT', 'SMART_TRAIL_2'],
    ['ALGO_PRO_SHORT', 'ALGO_PRO_LONG'],
    ['TARGET_TREND_SHORT', 'TARGET_TREND_LONG'],
  ],
};
const AUTO_COMPANIONS = {
  LONG: ['LUX_LONG', 'COBRA_LONG', 'VGB_LONG', 'VGB_LONG_2', 'SMART_BAND_15', 'SMART_TRAIL_2', 'ALGO_PRO_LONG'],
  SHORT: ['LUX_SHORT', 'COBRA_SHORT', 'VGB_SHORT', 'VGB_SHORT_2', 'SMART_BAND_15_SHORT', 'SMART_TRAIL_2_SHORT', 'ALGO_PRO_SHORT'],
};

// Pools for randomized companion context
const RAND_TIMEFRAMES = ['1m', '2m', '30s', '45s'];
const RAND_ASSETS = ['NQ', 'QQQ', 'GC', 'CL', 'ZN'];
const RAND_SESSIONS = ['ETH', 'RTH'];
const _pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

function getDefaultDates() {
  const now = new Date();
  const y = now.getFullYear();
  return { start: `${y}-01-01`, end: now.toISOString().slice(0, 10) };
}

const StepChip = ({ num, label, color, signal, onPick, onClear, enabled, subtitle }) => (
  <Box sx={{ opacity: enabled ? 1 : 0.3, pointerEvents: enabled ? 'auto' : 'none' }}>
    <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.5 }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: signal ? color : alpha(color, 0.12), border: `2px solid ${signal ? color : alpha(color, 0.3)}` }}>
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: signal ? '#000' : color, fontFamily: MONO }}>{num}</Typography>
      </Box>
      <Typography sx={{ color: signal ? color : alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>{label}</Typography>
      {signal ? (
        <Stack direction="row" alignItems="center" spacing={0.3}>
          <Chip label={signal} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, fontFamily: MONO,
            bgcolor: alpha(color, 0.15), color, border: `1px solid ${alpha(color, 0.3)}`, '& .MuiChip-label': { px: 0.6 } }} />
          {subtitle && <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.52rem', fontFamily: MONO }}>{subtitle}</Typography>}
          <IconButton size="small" onClick={onClear} sx={{ color: alpha(color, 0.5), p: 0.2 }}><CloseIcon sx={{ fontSize: 12 }} /></IconButton>
        </Stack>
      ) : (
        <Button size="small" onClick={onPick} sx={{ fontSize: '0.58rem', fontFamily: MONO, color: alpha(color, 0.6),
          textTransform: 'none', border: `1px dashed ${alpha(color, 0.2)}`, borderRadius: 1, px: 1.5, minWidth: 0,
          '&:hover': { bgcolor: alpha(color, 0.06), borderColor: alpha(color, 0.4) } }}>Pick</Button>
      )}
    </Stack>
  </Box>
);

export default function StrategyGenerator() {
  // Signal state
  const [entrySignal, setEntrySignal] = useState(null);
  const [exitSignal, setExitSignal] = useState(null);
  const [companions, setCompanions] = useState([]);
  const [companionMode, setCompanionMode] = useState('smart');
  const [filters, setFilters] = useState([]);
  const [direction, setDirection] = useState('LONG');
  const [asset, setAsset] = useState('NQ');
  const [timeframe, setTimeframe] = useState('1m');
  const [session, setSession] = useState('ETH');
  const [startDate, setStartDate] = useState(getDefaultDates().start);
  const [endDate, setEndDate] = useState(getDefaultDates().end);
  const [limit, setLimit] = useState('0');

  // Discovery params
  const [mode, setMode] = useState('manual'); // 'manual' | 'semi' | 'auto'
  const [minWinRate, setMinWinRate] = useState('80');
  const [minTrades, setMinTrades] = useState('5');
  const [minAvgReturn, setMinAvgReturn] = useState('');
  const [minTotalReturn, setMinTotalReturn] = useState('');
  // Randomize companion context (mix timeframes, assets, sessions) in SEMI/AUTO.
  // When enabled, each companion is assigned a random (timeframe, asset, session)
  // instead of inheriting the entry's context.
  const [randomCompanionContext, setRandomCompanionContext] = useState(false);

  // Picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRole, setPickerRole] = useState(null);

  // Results
  const [discovering, setDiscovering] = useState(false);
  const [progress, setProgress] = useState(null); // {current, total, label}
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [configModal, setConfigModal] = useState({ open: false, json: null, stratName: null, copied: false });
  const [resultsTab, setResultsTab] = useState(0);
  const [copiedConfigIdx, setCopiedConfigIdx] = useState(null);

  const openPicker = (role) => { setPickerRole(role); setPickerOpen(true); };
  const closePicker = () => { setPickerOpen(false); setPickerRole(null); };

  const handlePick = (picked) => {
    if (pickerRole === 'entry') {
      if (typeof picked === 'object' && picked?.signal) {
        setEntrySignal(picked.signal);
        setTimeframe(picked.timeframe);
        setAsset(picked.asset);
        setSession(picked.session);
      } else {
        setEntrySignal(typeof picked === 'string' ? picked : picked?.preceding || picked);
      }
    }
    else if (pickerRole === 'exit') {
      setExitSignal(typeof picked === 'object' && picked?.signal ? picked.signal : (typeof picked === 'string' ? picked : picked?.preceding || picked));
    }
    else if (pickerRole === 'companion') {
      if (typeof picked === 'object' && picked?.signal) {
        const key = `${picked.signal}|${picked.timeframe}|${picked.asset}|${picked.session}`;
        setCompanions(prev => prev.some(c => `${c.signal}|${c.timeframe}|${c.asset}|${c.session}` === key) ? prev : [...prev, picked]);
      } else {
        const sig = typeof picked === 'string' ? picked : picked?.preceding || picked;
        setCompanions(prev => prev.some(c => c.signal === sig && c.timeframe === timeframe && c.asset === asset && c.session === session) ? prev : [...prev, { signal: sig, timeframe, asset, session }]);
      }
    }
    else if (pickerRole === 'filter') {
      if (typeof picked === 'object' && picked?.preceding) {
        setFilters(prev => [...prev, {
          preceding_signal: picked.preceding,
          exclude_signal: picked.exclude || null,
          type: 'sequence_exclusion_with_equality',
        }]);
      } else if (typeof picked === 'string') {
        setFilters(prev => [...prev, { preceding_signal: picked, exclude_signal: null, type: 'sequence_exclusion_with_equality' }]);
      }
    }
  };

  const removeCompanion = (idx) => setCompanions(prev => prev.filter((_, i) => i !== idx));
  const removeFilter = (idx) => setFilters(prev => prev.filter((_, i) => i !== idx));
  const isCompanionMode = pickerRole === 'companion';
  const isFilterMode = pickerRole === 'filter';
  const roleColor = pickerRole === 'entry' ? '#22c55e' : pickerRole === 'exit' ? '#ef4444' : pickerRole === 'companion' ? '#00d4ff' : FILTER_COLOR;
  const roleLabel = pickerRole === 'entry' ? 'ENTRY' : pickerRole === 'exit' ? 'EXIT' : pickerRole === 'companion' ? 'COMPANION' : 'FILTER';
  const canGenerate = mode === 'auto' ? true
    : mode === 'semi' ? (entrySignal && exitSignal)
    : (entrySignal && exitSignal && companions.length > 0);

  const payloadFilters = filters.filter(f => f.preceding_signal).map(f => ({
    type: f.type, preceding_signal: f.preceding_signal,
    ...(f.exclude_signal ? { exclude_signal: f.exclude_signal } : {}),
  }));

  const tfSx = {
    '& .MuiOutlinedInput-root': { bgcolor: alpha('#000', 0.3), fontSize: '0.72rem', fontFamily: MONO, color: '#e2e8f0', borderRadius: 1.5, '& fieldset': { borderColor: alpha('#fff', 0.08) } },
    '& input': { py: 0.7, px: 1 }, '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.6)' },
  };

  // ── Generate Config ───────────────────────────────────────
  const generateStrategyConfig = (strat) => {
    const parseTimeStr = (s) => {
      const [h, m] = (s || '0:0').split(':').map(Number);
      return { hour: isNaN(h) ? 0 : h, minute: isNaN(m) ? 0 : m };
    };
    const tw = strat.time_window || {};
    const startT = parseTimeStr(tw.start);
    const endT = parseTimeStr(tw.end);

    const entryName = strat.entry_signal || entrySignal;
    const exitName = strat.exit_signal || exitSignal;

    // Resolve timeframe/session per condition from its context string ("1m · NQ · ETH")
    const getSignalObj = (c) => {
      let condTf = timeframe, condSession = session;
      if (c.context) {
        const parts = c.context.split(/\s*[·\u00b7]\s*/);
        if (parts.length >= 3) { condTf = parts[0].trim(); condSession = parts[2].trim(); }
        else if (parts.length >= 1) { condTf = parts[0].trim(); }
      }
      return { name: c.signal, timeframe: condTf, trading_hour: condSession };
    };

    const triggerObj = { name: entryName, timeframe, trading_hour: session };
    const exitObj = { name: exitName, timeframe, trading_hour: session };
    const subStrategies = [];
    const requiredIds = [];
    const confirmationIds = [];

    if (strat.type === 'TRIPLE' || strat.type === 'CONFIRMATION') {
      // Each companion condition is time_bound: companion fires before trigger
      (strat.conditions || []).forEach((c, i) => {
        const sigObj = getSignalObj(c);
        const sid = `C${i + 1}-${c.signal.replace(/[^A-Z0-9]/gi, '-')}-TO-${(entryName || 'ENTRY').replace(/[^A-Z0-9]/gi, '-')}`;
        subStrategies.push({
          condition: {
            type: 'time_bound',
            min_minutes: c.offset_min ?? 0,
            max_minutes: c.offset_max ?? 60,
            early_signal: sigObj,
            confirmation_signal: triggerObj,
          },
          strategy_id: sid,
        });
        confirmationIds.push(sid);
      });
    } else {
      // REQUIRED: cond[0] fires sequentially before cond[1]
      const c0 = strat.conditions?.[0] || {};
      const c1 = strat.conditions?.[1] || {};
      const sid = `R1-${(c0.signal || 'SIG-A').replace(/[^A-Z0-9]/gi, '-')}-TO-${(c1.signal || 'SIG-B').replace(/[^A-Z0-9]/gi, '-')}`;
      subStrategies.push({
        condition: {
          type: 'sequence_exclusion_with_equality',
          signals: [
            { ...getSignalObj(c0), order: 1 },
            { ...getSignalObj(c1), order: 2 },
          ],
          exclude_signals: [],
        },
        strategy_id: sid,
      });
      requiredIds.push(sid);
    }

    const twLabel = `${tw.start || ''}\u2013${tw.end || ''}`;
    const desc = `${asset} ${direction} ${entryName || ''} | ${strat.type} | ${twLabel} ET | ${strat.win_rate}% WR, ${strat.trade_count} trades`;
    const bulkId = `${asset} ${direction} ${entryName || 'STRAT'} | ${strat.type} ${twLabel}`;

    const config = {
      symbol: asset,
      direction,
      is_active: true,
      timeframe,
      active_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      description: desc,
      exit_signal: exitObj,
      trading_hour: session,
      sub_strategies: subStrategies,
      trigger_signal: entryName,
      trading_enabled: true,
      bulk_strategy_id: bulkId,
      time_restriction: {
        hour_start: startT.hour,
        minute_start: startT.minute,
        hour_end: endT.hour,
        minute_end: endT.minute,
        timezone: 'America/New_York',
      },
      required_strategies: requiredIds,
      confirmation_strategies: confirmationIds,
      confirmation_time_window_seconds: 30,
    };
    return JSON.stringify(config, null, 2);
  };

  // ── Discovery ─────────────────────────────────────────────
  const _baseDiscoverPayload = () => ({
    direction, timeframe, asset,
    trading_hour: session,
    start_date: startDate,
    end_date: endDate,
    limit: parseInt(limit) || 0,
    companion_mode: companionMode,
    min_win_rate: parseFloat(minWinRate) || 80,
    min_trades: parseInt(minTrades) || 5,
    ...(minAvgReturn !== '' && !isNaN(parseFloat(minAvgReturn)) ? { min_avg_return: parseFloat(minAvgReturn) } : {}),
    ...(minTotalReturn !== '' && !isNaN(parseFloat(minTotalReturn)) ? { min_total_return: parseFloat(minTotalReturn) } : {}),
  });

  const _callDiscover = async (payload) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(DISCOVER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  // True return = sum of (pnl_pct - SLIPPAGE) / 100 across the strategy's trades.
  // This matches the "Total return" line in the Excel sheet.
  const _trueReturn = (s) => {
    if (!s?.trades?.length) return 0;
    let sum = 0;
    for (const t of s.trades) {
      if (t.pnl_pct != null) sum += (t.pnl_pct - SLIPPAGE) / 100;
    }
    return sum;
  };

  // For each (entry_signal, time_window) keep only the single best strategy,
  // ranked by: win_rate desc → true return desc → trade_count desc.
  // Each strategy also gets a `true_return` field for sorting and display.
  const _optimizeStrategies = (list) => {
    const best = new Map();
    for (const s of list) {
      const tr = _trueReturn(s);
      const enriched = { ...s, true_return: +tr.toFixed(6) };
      const key = `${s.entry_signal || ''}|${s.time_window?.start || ''}|${s.time_window?.end || ''}`;
      const existing = best.get(key);
      if (!existing) { best.set(key, enriched); continue; }
      const better =
        (enriched.win_rate || 0) > (existing.win_rate || 0) ||
        ((enriched.win_rate || 0) === (existing.win_rate || 0) && enriched.true_return > existing.true_return) ||
        ((enriched.win_rate || 0) === (existing.win_rate || 0) && enriched.true_return === existing.true_return && (enriched.trade_count || 0) > (existing.trade_count || 0));
      if (better) best.set(key, enriched);
    }
    // Sort final list globally: WR → true_return → trade_count
    return Array.from(best.values()).sort((a, b) =>
      (b.win_rate || 0) - (a.win_rate || 0) ||
      (b.true_return || 0) - (a.true_return || 0) ||
      (b.trade_count || 0) - (a.trade_count || 0)
    );
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setDiscovering(true); setError(null); setResult(null); setProgress(null);

    try {
      if (mode === 'manual' || mode === 'semi') {
        // In SEMI mode: user supplied entry/exit; the system picks the companion set.
        const effectiveCompanions = mode === 'semi'
          ? (AUTO_COMPANIONS[direction] || AUTO_COMPANIONS.LONG)
              .filter(name => name !== entrySignal && name !== exitSignal)
              .map(name => randomCompanionContext
                ? { signal: name, timeframe: _pickRandom(RAND_TIMEFRAMES), asset: _pickRandom(RAND_ASSETS), session: _pickRandom(RAND_SESSIONS) }
                : { signal: name, timeframe, asset, session }
              )
          : companions.map(c => ({ signal: c.signal, timeframe: c.timeframe, asset: c.asset, session: c.session }));

        const payload = {
          ..._baseDiscoverPayload(),
          entry_signal: entrySignal,
          exit_signal: exitSignal,
          companion_signals: effectiveCompanions,
          ...(mode === 'semi' ? { strategy_types: ['triple'] } : {}),
          ...(payloadFilters.length > 0 ? { filters: payloadFilters } : {}),
        };
        const data = await _callDiscover(payload);
        // Tag each strategy with its entry signal then keep only the best
        // strategy per (entry, time window). Result is the optimized list.
        (data.strategies || []).forEach(s => { s.entry_signal = s.entry_signal || entrySignal; });
        const optimized = _optimizeStrategies(data.strategies || [])
          .slice(0, 50)
          .map((s, idx) => ({ ...s, id: idx + 1 }));
        data.strategies = optimized;
        data.strategy_count = optimized.length;
        // Bundle raw trades keyed by entry signal for the Excel export
        data.raw_trades_by_entry = data.raw_trades ? [{
          entry_signal: data.entry_signal || entrySignal,
          entry_label: data.entry_label || entrySignal,
          entry_context: data.entry_context || '',
          exit_label: data.exit_label || exitSignal,
          trades: data.raw_trades,
          companion_signals: data.companion_signals || [],
          companion_columns: data.companion_columns || [],
        }] : [];
        setResult(data);
        if (data.strategies?.length) {
          setSnackbar({ open: true, message: `${data.strategy_count} strategies discovered!`, severity: 'success' });
        } else {
          setSnackbar({ open: true, message: 'No strategies met the criteria. Try lowering win rate or min trades.', severity: 'warning' });
        }
      } else {
        // AUTO mode — iterate curated entries client-side
        const entries = AUTO_ENTRIES[direction] || AUTO_ENTRIES.LONG;
        const companionNames = AUTO_COMPANIONS[direction] || AUTO_COMPANIONS.LONG;
        const allStrategies = [];
        const rawByEntry = [];
        let mergedCompanionSignals = [];
        let mergedCompanionColumns = [];
        const errors = [];
        let baseTrades = 0;
        let baseWr = 0;

        for (let i = 0; i < entries.length; i++) {
          const [entry_sig, exit_sig] = entries[i];
          setProgress({ current: i + 1, total: entries.length, label: entry_sig });

          const scanCompanions = companionNames
            .filter(n => n !== entry_sig)
            .map(n => randomCompanionContext
              ? { signal: n, timeframe: _pickRandom(RAND_TIMEFRAMES), asset: _pickRandom(RAND_ASSETS), session: _pickRandom(RAND_SESSIONS) }
              : { signal: n, timeframe, asset, session }
            );

          const payload = {
            ..._baseDiscoverPayload(),
            entry_signal: entry_sig,
            exit_signal: exit_sig,
            companion_signals: scanCompanions,
            strategy_types: ['triple'],
          };

          try {
            const data = await _callDiscover(payload);
            baseTrades += data.base_trade_count || 0;
            baseWr = data.base_win_rate || baseWr;
            if (data.companion_signals?.length && mergedCompanionSignals.length < data.companion_signals.length) {
              mergedCompanionSignals = data.companion_signals;
              mergedCompanionColumns = data.companion_columns || [];
            }
            if (data.raw_trades?.length) {
              rawByEntry.push({
                entry_signal: entry_sig,
                entry_label: data.entry_label || entry_sig,
                entry_context: data.entry_context || '',
                exit_label: data.exit_label || exit_sig,
                trades: data.raw_trades,
                companion_signals: data.companion_signals || [],
                companion_columns: data.companion_columns || [],
              });
            }
            (data.strategies || []).forEach(s => {
              s.entry_signal = entry_sig;
              s.exit_signal = exit_sig;
              allStrategies.push(s);
            });
          } catch (err) {
            errors.push(`${entry_sig}: ${err.message}`);
          }
        }

        // Keep only the best strategy per (entry, time window), ranked by
        // win_rate → true_return (slippage-adjusted) → trade_count.
        const top = _optimizeStrategies(allStrategies)
          .slice(0, 50)
          .map((s, idx) => ({ ...s, id: idx + 1 }));

        setResult({
          strategies: top,
          strategy_count: top.length,
          scanned_entries: entries.length,
          errors,
          base_trade_count: baseTrades,
          base_win_rate: baseWr,
          direction,
          companion_signals: mergedCompanionSignals,
          companion_columns: mergedCompanionColumns,
          raw_trades_by_entry: rawByEntry,
        });

        if (top.length) {
          setSnackbar({ open: true, message: `${top.length} strategies found across ${entries.length} entry signals!`, severity: 'success' });
        } else {
          setSnackbar({ open: true, message: 'No strategies met the criteria. Try lowering win rate or min trades.', severity: 'warning' });
        }
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    }
    setDiscovering(false);
    setProgress(null);
  };

  // ── Excel Export ──────────────────────────────────────────
  const handleDownloadExcel = () => {
    if (!result?.strategies?.length) return;
    const wb = XLSX.utils.book_new();
    const cleanDt = (iso) => {
      if (!iso) return '';
      const p = iso.split('T');
      return `${p[0] || ''} ${(p[1] || '').replace(/[+-]\d{2}:\d{2}$/, '').replace('Z', '').slice(0, 5)}`;
    };

    // Average (slippage-adjusted) column — matches Trading Data export:
    // Average = (pnl_pct - SLIPPAGE) / 100
    const avgValue = (t) => (t.pnl_pct != null ? +((t.pnl_pct - SLIPPAGE) / 100).toFixed(6) : null);

    // Build one trade row identical to the raw export. Used by both raw sheets
    // and strategy sheets so trade rows in a strategy are exact copies of the
    // raw rows, just picked out and stacked.
    const buildTradeRow = (t, idx, cSigs) => {
      const ex = t.excursion || {};
      const row = [
        idx,
        result.direction,
        (t.entry_time?.split('T') || [''])[0],
      ];
      cSigs.forEach(sig => {
        const comp = t.companions?.[sig];
        row.push(comp?.time ? cleanDt(comp.time) : '');
      });
      row.push(
        cleanDt(t.entry_time),
        t.entry_price,
        cleanDt(t.exit_time),
        t.exit_price,
        t.duration_human || '',
        t.pnl_points != null ? t.pnl_points : '',
        t.pnl_pct != null ? +(t.pnl_pct / 100).toFixed(6) : '',
        avgValue(t) != null ? avgValue(t) : '',
        ex.drawdown_pct != null ? +(ex.drawdown_pct / 100).toFixed(6) : '',
        ex.lowest_point ?? '',
        ex.drawdown_time ? cleanDt(ex.drawdown_time) : '',
        ex.runup_pct != null ? +(ex.runup_pct / 100).toFixed(6) : '',
        ex.highest_point ?? '',
        ex.runup_time ? cleanDt(ex.runup_time) : '',
      );
      return row;
    };

    const buildTradeHeader = (cHeaders) => [
      '#', 'Trade', 'Date', ...cHeaders, 'Entry', 'Entry $', 'Exit', 'Exit $',
      'Duration', 'P&L', 'P&L %', 'Average',
      'Drawdown %', 'Lowest Point', 'Drawdown Time',
      'Run-up %', 'Highest Point', 'Highest Point Time',
    ];

    const buildCompanionHeaders = (cSigs, cCols) => cSigs.map((sig, i) => {
      const cc = cCols[i];
      if (!cc) return sig;
      const sub = [cc.timeframe, cc.asset, cc.session].filter(Boolean);
      return sub.length ? `${sig} (${sub.join(' \u00b7 ')})` : sig;
    });

    // ── First sheets: raw trades used to generate the strategies ──
    const rawBundles = result.raw_trades_by_entry || [];
    rawBundles.forEach((bundle, bIdx) => {
      const cSigs = bundle.companion_signals || [];
      const cCols = bundle.companion_columns || [];
      const cHeaders = buildCompanionHeaders(cSigs, cCols);
      const rows = [buildTradeHeader(cHeaders)];
      (bundle.trades || []).forEach((t, ti) => rows.push(buildTradeRow(t, ti + 1, cSigs)));
      const ws = XLSX.utils.aoa_to_sheet(rows);
      const tag = bundle.entry_signal || `entry${bIdx + 1}`;
      const name = `Raw - ${tag}`.slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, name);
    });

    result.strategies.forEach((strat, idx) => {
      const rows = [];
      const tw = strat.time_window;
      const conds = strat.conditions || [];
      const cond0 = conds[0] || {};

      const entryLabel = strat.entry_label || strat.entry_signal || '';
      const entryCtx = strat.entry_context || '';
      const exitLabel = strat.exit_label || strat.exit_signal || '';
      const entryDesc = entryLabel ? (entryCtx ? `${entryLabel} (${entryCtx})` : entryLabel) : '';
      const exitDesc = exitLabel ? (entryCtx ? `${exitLabel} (${entryCtx})` : exitLabel) : '';

      rows.push([`${tw.start}\u2013${tw.end}`, strat.instruction]);
      rows.push([`${strat.win_rate}% WR \u2014 ${strat.trade_count} trades`]);
      rows.push([]);
      if (entryDesc) rows.push([`Entry signal: ${entryDesc}`]);
      if (exitDesc) rows.push([`Exit signal: ${exitDesc}`]);
      rows.push([`Direction: ${result.direction}`]);
      rows.push([]);
      rows.push([`Condition type: ${strat.type}`, `Instructions \u2014 take the ${result.direction} ${entryLabel} trade only when ALL conditions are met:`]);

      if (strat.type === 'TRIPLE') {
        rows.push([`  1.  Only take trades where Entry Time is between ${tw.start} and ${tw.end} ET`]);
        conds.forEach((c, k) => {
          rows.push([`  ${k + 2}.  ${c.signal_label} (${c.context}) must have fired ${c.offset_min}\u2013${c.offset_max} minutes before entry`]);
        });
        rows.push([`  ${conds.length + 2}.  All ${conds.length + 1} conditions must be met before taking the trade.`]);
      } else if (strat.type === 'CONFIRMATION') {
        rows.push([`  1.  Only take trades where Entry Time is between ${tw.start} and ${tw.end} ET`]);
        rows.push([`  2.  CONFIRMATION \u2014 ${cond0.signal_label} must have fired ${cond0.offset_min}\u2013${cond0.offset_max} minutes before entry`]);
        rows.push([`      Meaning: Entry Time minus ${cond0.signal_label} timestamp = ${cond0.offset_min}\u2013${cond0.offset_max} min`]);
        rows.push([`  3.  Both conditions must be met before taking the trade.`]);
      } else {
        const cond1 = conds[1] || {};
        rows.push([`  1.  Only take trades where Entry Time is between ${tw.start} and ${tw.end} ET`]);
        rows.push([`  2.  REQUIRED \u2014 ${cond0.signal_label} must have fired ${cond0.offset_min}\u2013${cond0.offset_max} minutes before ${cond1.signal_label}`]);
        rows.push([`      Meaning: ${cond1.signal_label} timestamp minus ${cond0.signal_label} timestamp = ${cond0.offset_min}\u2013${cond0.offset_max} min`]);
        rows.push([`      Both signals must have fired at or before your entry.`]);
        rows.push([`  3.  Both conditions must be met before taking the trade.`]);
      }

      rows.push([]);
      rows.push(['Trade List']);

      // Use the exact companion layout from the raw sheet so the trade rows
      // we stack here are byte-for-byte identical to their raw counterparts.
      const companionSigs = result.companion_signals || [];
      const companionCols = result.companion_columns || [];
      const compHeaders = buildCompanionHeaders(companionSigs, companionCols);

      // Base header = raw sheet header. Append one offset column per condition,
      // then the Result column at the very end.
      const baseHeader = buildTradeHeader(compHeaders);
      const header = [...baseHeader];
      if (strat.type === 'TRIPLE') {
        conds.forEach(c => header.push(`${c.signal_label} \u2192 Entry (mins)`));
      } else if (strat.type === 'CONFIRMATION') {
        header.push(`${cond0.signal_label} \u2192 Entry (mins)`);
      } else {
        const cond1 = conds[1] || {};
        header.push(`${cond0.signal_label} \u2192 ${cond1.signal_label} (mins)`);
      }
      header.push('Result');
      rows.push(header);

      let wins = 0, losses = 0;
      let totalAvg = 0; // slippage-adjusted sum across this strategy's trades
      strat.trades.forEach((t, ti) => {
        // Exact raw row, same columns and same values
        const row = buildTradeRow(t, ti + 1, companionSigs);

        // Append offset(s) per condition
        if (strat.type === 'TRIPLE') {
          conds.forEach(c => {
            const comp = t.companions?.[c.signal];
            if (comp?.time && t.entry_time) {
              try {
                const diff = (new Date(t.entry_time) - new Date(comp.time)) / 60000;
                row.push(`${diff.toFixed(1)} mins`);
              } catch { row.push(''); }
            } else { row.push(''); }
          });
        } else if (strat.type === 'CONFIRMATION') {
          const comp = t.companions?.[cond0.signal];
          if (comp?.time && t.entry_time) {
            try {
              const diff = (new Date(t.entry_time) - new Date(comp.time)) / 60000;
              row.push(`${diff.toFixed(1)} mins`);
            } catch { row.push(''); }
          } else { row.push(''); }
        } else {
          const cond1 = conds[1] || {};
          const compA = t.companions?.[cond0.signal];
          const compB = t.companions?.[cond1.signal];
          if (compA?.time && compB?.time) {
            try {
              const diff = (new Date(compB.time) - new Date(compA.time)) / 60000;
              row.push(`${diff.toFixed(1)} mins`);
            } catch { row.push(''); }
          } else { row.push(''); }
        }

        const res = t.status === 'WIN' ? 'WIN' : 'LOSS';
        row.push(res);
        if (res === 'WIN') wins++; else losses++;
        const av = avgValue(t);
        if (av != null) totalAvg += av;
        rows.push(row);
      });

      // Summary row: total return = sum of Average column (slippage-adjusted)
      const summaryCols = header.length;
      const summary = new Array(summaryCols).fill('');
      summary[0] = `Total: ${strat.trade_count} trades`;
      summary[summaryCols - 2] = `${wins}W  /  ${losses}L`;
      summary[summaryCols - 1] = `${strat.win_rate}%`;
      rows.push(summary);

      // Total return row from the Average column (pnl_pct - 0.05 slippage, /100)
      const totalRow = new Array(summaryCols).fill('');
      totalRow[0] = 'Total return (Average sum, slippage-adjusted):';
      totalRow[summaryCols - 1] = +totalAvg.toFixed(6);
      rows.push(totalRow);

      const ws = XLSX.utils.aoa_to_sheet(rows);
      const typeAbbr = strat.type === 'TRIPLE' ? 'Tri' : strat.type === 'CONFIRMATION' ? 'Conf' : 'Req';
      const twLabel = `${tw.start.replace(':', '')}-${tw.end.replace(':', '')}`;
      const sheetName = `${idx + 1} ${typeAbbr} ${twLabel} WR${Math.round(strat.win_rate)}`.slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    if (wb.SheetNames.length === 0) return;
    XLSX.writeFile(wb, `strategies_${entrySignal}_${asset}_${startDate}.xlsx`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', marginTop: '50px' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesomeIcon sx={{ color: '#f59e0b', fontSize: 28 }} />
          <Box>
            <Typography sx={{ color: '#e2e8f0', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '-0.02em' }}>Strategy Generator</Typography>
            <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.65rem', fontFamily: MONO, letterSpacing: '0.1em' }}>PICK SIGNALS &rarr; SET CRITERIA &rarr; DISCOVER HIGH WIN-RATE STRATEGIES</Typography>
          </Box>
        </Stack>
      </Stack>

      {/* ═══ CONFIG PANEL ═══ */}
      <Paper elevation={0} sx={{ bgcolor: alpha('#0a0e17', 0.95), border: `1px solid ${alpha('#f59e0b', 0.1)}`, borderRadius: 2.5, p: 2.5, mb: 2.5 }}>
        <Stack spacing={2}>
          {/* Mode toggle: Manual / Semi / Auto */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[
              { key: 'manual', label: 'MANUAL', desc: 'You pick entry, exit, and companions', color: '#a855f7' },
              { key: 'semi', label: 'SEMI', desc: 'You pick entry & exit — system guesses the rest', color: '#00d4ff' },
              { key: 'auto', label: 'AUTO', desc: 'One-click — system scans everything', color: '#f59e0b' },
            ].map(m => (
              <Paper key={m.key} elevation={0} onClick={() => setMode(m.key)}
                sx={{ flex: 1, p: 1.3, cursor: 'pointer', borderRadius: 2,
                  bgcolor: mode === m.key ? alpha(m.color, 0.08) : alpha('#000', 0.3),
                  border: `1px solid ${mode === m.key ? alpha(m.color, 0.5) : alpha('#fff', 0.06)}`,
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: alpha(m.color, 0.3) } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%',
                    bgcolor: mode === m.key ? m.color : 'transparent',
                    border: `2px solid ${mode === m.key ? m.color : alpha('#fff', 0.15)}` }} />
                  <Box>
                    <Typography sx={{ color: mode === m.key ? m.color : alpha('#fff', 0.5), fontSize: '0.7rem', fontWeight: 800, fontFamily: MONO, letterSpacing: '0.1em' }}>{m.label}</Typography>
                    <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.5rem', fontFamily: MONO }}>{m.desc}</Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>

          {/* Entry / Exit — shown in Manual and Semi modes */}
          {(mode === 'manual' || mode === 'semi') && (
            <>
          <StepChip num="1" label="ENTRY SIGNAL" color="#22c55e" signal={entrySignal} onPick={() => openPicker('entry')} onClear={() => setEntrySignal(null)} enabled={true}
            subtitle={entrySignal ? `${timeframe} \u00b7 ${asset} \u00b7 ${session}` : null} />
          <StepChip num="2" label="EXIT SIGNAL" color="#ef4444" signal={exitSignal} onPick={() => openPicker('exit')} onClear={() => setExitSignal(null)} enabled={!!entrySignal} />
            </>
          )}

          {/* Semi-mode hint */}
          {mode === 'semi' && entrySignal && exitSignal && (
            <Paper elevation={0} sx={{ bgcolor: alpha('#00d4ff', 0.05), border: `1px dashed ${alpha('#00d4ff', 0.25)}`, borderRadius: 1.5, px: 1.5, py: 1 }}>
              <Typography sx={{ color: '#00d4ff', fontSize: '0.58rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.05em' }}>
                SEMI MODE &mdash; system will scan these companions automatically:
              </Typography>
              <Typography sx={{ color: alpha('#00d4ff', 0.7), fontSize: '0.55rem', fontFamily: MONO, mt: 0.3 }}>
                {(AUTO_COMPANIONS[direction] || AUTO_COMPANIONS.LONG)
                  .filter(n => n !== entrySignal && n !== exitSignal)
                  .join(', ')}
              </Typography>
              <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.5rem', fontFamily: MONO, mt: 0.4 }}>
                {randomCompanionContext
                  ? 'Each companion gets a random timeframe/asset/session. Discovery looks for 3-condition (TRIPLE) strategies.'
                  : 'Each uses the same timeframe/asset/session as your entry. Discovery looks for 3-condition (TRIPLE) strategies.'}
              </Typography>
            </Paper>
          )}

          {/* Randomize companion context toggle (SEMI / AUTO only) */}
          {(mode === 'semi' || mode === 'auto') && (
            <Paper elevation={0} onClick={() => setRandomCompanionContext(v => !v)}
              sx={{ px: 1.5, py: 1, cursor: 'pointer', borderRadius: 1.5,
                bgcolor: randomCompanionContext ? alpha('#a855f7', 0.08) : alpha('#000', 0.3),
                border: `1px solid ${randomCompanionContext ? alpha('#a855f7', 0.5) : alpha('#fff', 0.06)}`,
                transition: 'all 0.2s',
                '&:hover': { borderColor: alpha('#a855f7', 0.3) } }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5,
                  bgcolor: randomCompanionContext ? '#a855f7' : 'transparent',
                  border: `2px solid ${randomCompanionContext ? '#a855f7' : alpha('#fff', 0.15)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {randomCompanionContext && <Typography sx={{ fontSize: '0.55rem', color: '#000', fontWeight: 900 }}>&#10003;</Typography>}
                </Box>
                <Box>
                  <Typography sx={{ color: randomCompanionContext ? '#a855f7' : alpha('#fff', 0.6), fontSize: '0.65rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.05em' }}>
                    RANDOMIZE COMPANION CONTEXT
                  </Typography>
                  <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.5rem', fontFamily: MONO }}>
                    Mix timeframes (1m/30s/45s), assets (NQ/QQQ), and sessions (ETH/RTH) across companions
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Manual-only sections: companions / filters */}
          {mode === 'manual' && (
            <>
          {/* Companions */}
          <Box sx={{ opacity: exitSignal ? 1 : 0.3, pointerEvents: exitSignal ? 'auto' : 'none' }}>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.8 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: companions.length ? '#00d4ff' : alpha('#00d4ff', 0.12), border: `2px solid ${companions.length ? '#00d4ff' : alpha('#00d4ff', 0.3)}` }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: companions.length ? '#000' : '#00d4ff', fontFamily: MONO }}>3</Typography>
              </Box>
              <Typography sx={{ color: companions.length ? '#00d4ff' : alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>COMPANION SIGNALS</Typography>
              <Chip label="required" size="small" sx={{ height: 14, fontSize: '0.45rem', bgcolor: alpha('#ef4444', 0.08), color: '#ef4444', '& .MuiChip-label': { px: 0.4 } }} />
              <Box sx={{ ml: 'auto' }}>
                <Stack direction="row" spacing={0.3}>
                  {['simple', 'smart'].map(m => (
                    <Pill key={m} active={companionMode === m} activeColor={m === 'smart' ? '#22c55e' : '#94a3b8'} onClick={() => setCompanionMode(m)} sx={{ py: 0.2, px: 1, fontSize: '0.52rem', minWidth: 0 }}>
                      {m === 'smart' ? 'Smart' : 'Simple'}
                    </Pill>
                  ))}
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {companions.map((c, i) => (
                <Chip key={`${c.signal}-${c.timeframe}-${c.asset}-${c.session}-${i}`}
                  label={`${c.signal} (${c.timeframe} \u00b7 ${c.asset} \u00b7 ${c.session})`}
                  size="small" onDelete={() => removeCompanion(i)} sx={{ height: 24, fontSize: '0.65rem', fontWeight: 600, fontFamily: MONO,
                  bgcolor: alpha('#00d4ff', 0.12), color: '#00d4ff', border: `1px solid ${alpha('#00d4ff', 0.25)}`,
                  '& .MuiChip-label': { px: 0.7 }, '& .MuiChip-deleteIcon': { fontSize: 14, color: alpha('#00d4ff', 0.5) } }} />
              ))}
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />} onClick={() => openPicker('companion')}
                sx={{ fontSize: '0.58rem', fontFamily: MONO, color: alpha('#00d4ff', 0.6),
                  textTransform: 'none', border: `1px dashed ${alpha('#00d4ff', 0.2)}`, borderRadius: 1, px: 1.5, minWidth: 0,
                  '&:hover': { bgcolor: alpha('#00d4ff', 0.06), borderColor: alpha('#00d4ff', 0.4) } }}>
                Add Companion
              </Button>
            </Stack>
          </Box>

          {/* Sequence Filters */}
          <Box sx={{ opacity: exitSignal ? 1 : 0.3, pointerEvents: exitSignal ? 'auto' : 'none' }}>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.8 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: filters.length ? FILTER_COLOR : alpha(FILTER_COLOR, 0.12), border: `2px solid ${filters.length ? FILTER_COLOR : alpha(FILTER_COLOR, 0.3)}` }}>
                <FilterAltIcon sx={{ fontSize: 11, color: filters.length ? '#000' : FILTER_COLOR }} />
              </Box>
              <Typography sx={{ color: filters.length ? FILTER_COLOR : alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>FILTERS</Typography>
              <Chip label="optional" size="small" sx={{ height: 14, fontSize: '0.45rem', bgcolor: alpha('#fff', 0.04), color: alpha('#fff', 0.2), '& .MuiChip-label': { px: 0.4 } }} />
            </Stack>
            <Stack spacing={0.8}>
              {filters.map((f, idx) => (
                <Paper key={idx} elevation={0} sx={{ bgcolor: alpha(FILTER_COLOR, 0.04), border: `1px solid ${alpha(FILTER_COLOR, 0.12)}`, borderRadius: 1.5, px: 1, py: 0.7 }}>
                  <Stack direction="row" alignItems="center" spacing={0.6} flexWrap="wrap">
                    <Chip label={f.preceding_signal} size="small" sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, fontFamily: MONO,
                      bgcolor: alpha('#22c55e', 0.12), color: '#22c55e', border: `1px solid ${alpha('#22c55e', 0.25)}`, '& .MuiChip-label': { px: 0.6 } }} />
                    <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.55rem', fontFamily: MONO }}>&rarr; entry</Typography>
                    {f.exclude_signal && (
                      <>
                        <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.55rem', fontFamily: MONO }}>, no</Typography>
                        <Chip label={f.exclude_signal} size="small" sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, fontFamily: MONO,
                          bgcolor: alpha('#ef4444', 0.12), color: '#ef4444', border: `1px solid ${alpha('#ef4444', 0.25)}`, '& .MuiChip-label': { px: 0.6 } }} />
                        <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.55rem', fontFamily: MONO }}>between</Typography>
                      </>
                    )}
                    <IconButton size="small" onClick={() => removeFilter(idx)} sx={{ ml: 'auto', color: alpha('#fff', 0.2) }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
              <Button size="small" startIcon={<FilterAltIcon sx={{ fontSize: 12 }} />} onClick={() => openPicker('filter')}
                sx={{ alignSelf: 'flex-start', fontSize: '0.58rem', fontFamily: MONO, color: alpha(FILTER_COLOR, 0.6),
                  textTransform: 'none', border: `1px dashed ${alpha(FILTER_COLOR, 0.2)}`, borderRadius: 1, px: 1.5,
                  '&:hover': { bgcolor: alpha(FILTER_COLOR, 0.06), borderColor: alpha(FILTER_COLOR, 0.4) } }}>
                Add Filter
              </Button>
            </Stack>
          </Box>
            </>
          )}

          {/* Settings row */}
          <Box sx={{ borderTop: `1px solid ${alpha('#fff', 0.06)}`, pt: 2 }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              <Stack spacing={0.2}>
                <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.48rem', fontFamily: MONO, letterSpacing: '0.15em' }}>DIRECTION</Typography>
                <Stack direction="row" spacing={0.3}>
                  {['LONG', 'SHORT'].map(d => (
                    <Pill key={d} active={direction === d} activeColor={d === 'LONG' ? '#22c55e' : '#ef4444'} onClick={() => setDirection(d)} sx={{ py: 0.2, px: 1.2, fontSize: '0.55rem' }}>{d}</Pill>
                  ))}
                </Stack>
              </Stack>

              <Stack spacing={0.2}>
                <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.48rem', fontFamily: MONO, letterSpacing: '0.15em' }}>ASSET</Typography>
                <Stack direction="row" spacing={0.3}>
                  {['NQ', 'QQQ'].map(a => (
                    <Pill key={a} active={asset === a} activeColor="#a855f7" onClick={() => setAsset(a)} sx={{ py: 0.2, px: 1.2, fontSize: '0.55rem' }}>{a}</Pill>
                  ))}
                </Stack>
              </Stack>

              <Stack spacing={0.2}>
                <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.48rem', fontFamily: MONO, letterSpacing: '0.15em' }}>TIMEFRAME</Typography>
                <Stack direction="row" spacing={0.3}>
                  {['1m', '2m', '30s', '45s'].map(tf => (
                    <Pill key={tf} active={timeframe === tf} activeColor="#00d4ff" onClick={() => setTimeframe(tf)} sx={{ py: 0.2, px: 1, fontSize: '0.55rem' }}>{tf}</Pill>
                  ))}
                </Stack>
              </Stack>

              <Stack spacing={0.2}>
                <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.48rem', fontFamily: MONO, letterSpacing: '0.15em' }}>SESSION</Typography>
                <Stack direction="row" spacing={0.3}>
                  {['ETH', 'RTH'].map(s => (
                    <Pill key={s} active={session === s} activeColor="#f59e0b" onClick={() => setSession(s)} sx={{ py: 0.2, px: 1.2, fontSize: '0.55rem' }}>{s}</Pill>
                  ))}
                </Stack>
              </Stack>

              <Stack spacing={0.2}>
                <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.48rem', fontFamily: MONO, letterSpacing: '0.15em' }}>START DATE</Typography>
                <TextField type="date" value={startDate} onChange={e => setStartDate(e.target.value)} size="small" sx={{ width: 130, ...tfSx }} />
              </Stack>
              <Stack spacing={0.2}>
                <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.48rem', fontFamily: MONO, letterSpacing: '0.15em' }}>END DATE</Typography>
                <TextField type="date" value={endDate} onChange={e => setEndDate(e.target.value)} size="small" sx={{ width: 130, ...tfSx }} />
              </Stack>
              <Stack spacing={0.2}>
                <Typography sx={{ color: alpha('#fff', 0.25), fontSize: '0.48rem', fontFamily: MONO, letterSpacing: '0.15em' }}>LIMIT</Typography>
                <TextField value={limit} onChange={e => setLimit(e.target.value)} size="small" sx={{ width: 70, ...tfSx }} inputProps={{ style: { textAlign: 'center' } }} />
              </Stack>
            </Stack>
          </Box>

          {/* Discovery params */}
          <Box sx={{ borderTop: `1px solid ${alpha('#f59e0b', 0.1)}`, pt: 2 }}>
            <Typography sx={{ color: '#f59e0b', fontSize: '0.6rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em', mb: 1 }}>DISCOVERY CRITERIA</Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.55rem', fontFamily: MONO }}>Min Win Rate %</Typography>
                <TextField value={minWinRate} onChange={e => setMinWinRate(e.target.value)} size="small"
                  sx={{ width: 55, ...tfSx }} inputProps={{ style: { textAlign: 'center' } }} />
              </Stack>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.55rem', fontFamily: MONO }}>Min Trades</Typography>
                <TextField value={minTrades} onChange={e => setMinTrades(e.target.value)} size="small"
                  sx={{ width: 55, ...tfSx }} inputProps={{ style: { textAlign: 'center' } }} />
              </Stack>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Tooltip title="Minimum average P&L % per trade (leave empty to skip)">
                  <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.55rem', fontFamily: MONO, cursor: 'help' }}>Min Avg Return %</Typography>
                </Tooltip>
                <TextField value={minAvgReturn} onChange={e => setMinAvgReturn(e.target.value)} size="small" placeholder="—"
                  sx={{ width: 65, ...tfSx }} inputProps={{ style: { textAlign: 'center' } }} />
              </Stack>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Tooltip title="Minimum total cumulative P&L % across all trades in the strategy (leave empty to skip)">
                  <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.55rem', fontFamily: MONO, cursor: 'help' }}>Min Total Return %</Typography>
                </Tooltip>
                <TextField value={minTotalReturn} onChange={e => setMinTotalReturn(e.target.value)} size="small" placeholder="—"
                  sx={{ width: 65, ...tfSx }} inputProps={{ style: { textAlign: 'center' } }} />
              </Stack>
            </Stack>
          </Box>

          {/* Generate button */}
          <Button fullWidth variant="contained" onClick={handleGenerate} disabled={discovering || !canGenerate}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
            sx={{ bgcolor: '#f59e0b', color: '#000', fontWeight: 800, fontSize: '0.85rem', fontFamily: MONO, letterSpacing: '0.08em', borderRadius: 1.5, py: 1.4,
              textTransform: 'none', boxShadow: `0 4px 24px ${alpha('#f59e0b', 0.4)}`, '&:hover': { bgcolor: '#d97706' },
              '&.Mui-disabled': { bgcolor: alpha('#f59e0b', 0.15), color: alpha('#000', 0.3) } }}>
            {discovering
              ? (progress ? `SCANNING ${progress.current}/${progress.total} — ${progress.label}` : 'DISCOVERING STRATEGIES...')
              : (mode === 'auto' ? 'AUTO-GENERATE STRATEGIES' : mode === 'semi' ? 'SEMI-GENERATE STRATEGIES' : 'GENERATE STRATEGIES')}
          </Button>
          {discovering && (
            <LinearProgress
              variant={progress ? 'determinate' : 'indeterminate'}
              value={progress ? (progress.current / progress.total) * 100 : undefined}
              sx={{ borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' }, bgcolor: alpha('#f59e0b', 0.1) }} />
          )}
        </Stack>
      </Paper>

      {/* Error */}
      {error && (
        <Paper elevation={0} sx={{ bgcolor: alpha('#ef4444', 0.08), border: `1px solid ${alpha('#ef4444', 0.3)}`, borderRadius: 1.5, p: 1.5, mb: 2 }}>
          <Typography sx={{ color: '#ef4444', fontSize: '0.72rem', fontWeight: 600, fontFamily: MONO }}>{error}</Typography>
        </Paper>
      )}

      {/* ═══ RESULTS ═══ */}
      {result && (
        <Paper elevation={0} sx={{ bgcolor: alpha('#0a0e17', 0.95), border: `1px solid ${alpha('#f59e0b', 0.1)}`, borderRadius: 2.5, p: 2.5 }}>
          {/* Summary header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TrophyIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
              <Typography sx={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: 700, fontFamily: MONO }}>
                {result.strategy_count || 0} Strategies Found
              </Typography>
              <Chip label={`Base: ${result.base_trade_count} trades / ${result.base_win_rate}% WR`} size="small"
                sx={{ height: 20, fontSize: '0.52rem', fontFamily: MONO, bgcolor: alpha('#fff', 0.04), color: alpha('#fff', 0.4), '& .MuiChip-label': { px: 0.6 } }} />
            </Stack>
            <Stack direction="row" spacing={1}>
              {result.strategies?.length > 0 && resultsTab === 1 && (
                <Button size="small" startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    const all = (result.strategies || []).map(s => JSON.parse(generateStrategyConfig(s)));
                    const blob = new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url;
                    a.download = `configs_${entrySignal || 'strategies'}_${asset}_${startDate}.json`;
                    a.click(); URL.revokeObjectURL(url);
                  }}
                  sx={{ fontSize: '0.62rem', fontFamily: MONO, color: '#a855f7', textTransform: 'none',
                    border: `1px solid ${alpha('#a855f7', 0.3)}`, borderRadius: 1, px: 1.5,
                    '&:hover': { bgcolor: alpha('#a855f7', 0.08) } }}>
                  Download All JSON
                </Button>
              )}
              {result.strategies?.length > 0 && (
                <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 14 }} />} onClick={handleDownloadExcel}
                  sx={{ fontSize: '0.62rem', fontFamily: MONO, color: '#22c55e', textTransform: 'none',
                    border: `1px solid ${alpha('#22c55e', 0.3)}`, borderRadius: 1, px: 1.5,
                    '&:hover': { bgcolor: alpha('#22c55e', 0.08) } }}>
                  Download Excel
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Tabs */}
          <Tabs value={resultsTab} onChange={(_, v) => setResultsTab(v)}
            sx={{ mb: 2, minHeight: 32, borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
              '& .MuiTabs-indicator': { bgcolor: '#f59e0b' },
              '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.62rem', fontFamily: MONO, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'none', color: alpha('#fff', 0.4) },
              '& .Mui-selected': { color: '#f59e0b !important' } }}>
            <Tab label="Strategies" />
            <Tab label="Configs" />
          </Tabs>

          {/* Tab 0: Strategy cards */}
          {resultsTab === 0 && result.strategies?.length === 0 && (
            <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.75rem', fontFamily: MONO, textAlign: 'center', py: 4 }}>
              No strategies met the criteria. Try lowering the minimum win rate or minimum trades.
            </Typography>
          )}

          {resultsTab === 0 && <Stack spacing={1}>
            {(result.strategies || []).map((strat) => {
              const tw = strat.time_window;
              const cond0 = strat.conditions?.[0] || {};
              const wrColor = strat.win_rate >= 90 ? '#22c55e' : strat.win_rate >= 80 ? '#f59e0b' : '#ef4444';

              return (
                <Accordion key={strat.id} disableGutters elevation={0}
                  sx={{ bgcolor: alpha('#fff', 0.02), border: `1px solid ${alpha(wrColor, 0.15)}`, borderRadius: '8px !important',
                    '&::before': { display: 'none' }, '&.Mui-expanded': { borderColor: alpha(wrColor, 0.3) } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: alpha('#fff', 0.3), fontSize: 18 }} />}
                    sx={{ px: 2, py: 0.5, minHeight: 48 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', pr: 1 }}>
                      <Chip label={`#${strat.id}`} size="small" sx={{ height: 20, fontSize: '0.5rem', fontWeight: 800, fontFamily: MONO,
                        bgcolor: alpha(wrColor, 0.12), color: wrColor, '& .MuiChip-label': { px: 0.5 } }} />
                      <Chip label={strat.type} size="small" sx={{ height: 18, fontSize: '0.45rem', fontWeight: 700, fontFamily: MONO,
                        bgcolor: alpha('#a855f7', 0.1), color: '#a855f7', '& .MuiChip-label': { px: 0.5 } }} />
                      {strat.entry_label && (
                        <Chip label={strat.entry_label} size="small" sx={{ height: 18, fontSize: '0.45rem', fontWeight: 700, fontFamily: MONO,
                          bgcolor: alpha('#22c55e', 0.12), color: '#22c55e', border: `1px solid ${alpha('#22c55e', 0.25)}`, '& .MuiChip-label': { px: 0.5 } }} />
                      )}
                      <Typography sx={{ color: wrColor, fontSize: '0.78rem', fontWeight: 800, fontFamily: MONO }}>
                        {strat.win_rate}% WR
                      </Typography>
                      <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.6rem', fontFamily: MONO }}>
                        {strat.trade_count} trades ({strat.wins}W / {strat.losses}L)
                      </Typography>
                      {strat.avg_return != null && (
                        <Chip label={`avg ${strat.avg_return > 0 ? '+' : ''}${strat.avg_return}%`} size="small"
                          sx={{ height: 18, fontSize: '0.48rem', fontWeight: 700, fontFamily: MONO,
                            bgcolor: alpha(strat.avg_return >= 0 ? '#22c55e' : '#ef4444', 0.1),
                            color: strat.avg_return >= 0 ? '#22c55e' : '#ef4444',
                            '& .MuiChip-label': { px: 0.5 } }} />
                      )}
                      {strat.true_return != null && (
                        <Chip label={`ret ${strat.true_return > 0 ? '+' : ''}${(strat.true_return * 100).toFixed(3)}%`} size="small"
                          sx={{ height: 18, fontSize: '0.48rem', fontWeight: 700, fontFamily: MONO,
                            bgcolor: alpha(strat.true_return >= 0 ? '#22c55e' : '#ef4444', 0.12),
                            color: strat.true_return >= 0 ? '#22c55e' : '#ef4444',
                            '& .MuiChip-label': { px: 0.5 } }} />
                      )}
                      <Chip label={`${tw.start}\u2013${tw.end}`} size="small" sx={{ height: 18, fontSize: '0.48rem', fontFamily: MONO,
                        bgcolor: alpha('#00d4ff', 0.08), color: '#00d4ff', '& .MuiChip-label': { px: 0.5 } }} />
                      <Typography sx={{ color: alpha('#fff', 0.35), fontSize: '0.52rem', fontFamily: MONO, flex: 1, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {strat.conditions?.length > 1
                          ? `${strat.conditions.length} conditions`
                          : cond0.description}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Paper elevation={0} sx={{ bgcolor: alpha('#000', 0.3), borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
                      <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.62rem', fontFamily: MONO, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                        {strat.instruction}
                      </Typography>
                    </Paper>
                    {/* Trade list preview */}
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '90px 140px 140px 70px 70px 65px 100px 55px',
                        gap: 0.5, px: 1, py: 0.5, bgcolor: alpha('#fff', 0.02), borderRadius: 1 }}>
                        {['Date', 'Entry', 'Exit', 'Entry $', 'Exit $', 'P&L %', 'Offset', 'Result'].map(h => (
                          <Typography key={h} sx={{ color: alpha('#fff', 0.3), fontSize: '0.45rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.05em' }}>{h}</Typography>
                        ))}
                      </Box>
                      {strat.trades.map((t, ti) => {
                        const fmtDt = (iso) => { if (!iso) return '\u2014'; const p = iso.split('T'); return `${p[0]} ${(p[1]||'').slice(0,5)}`; };
                        const pnl = t.pnl_pct != null ? (t.pnl_pct / 100).toFixed(3) + '%' : '\u2014';
                        let offset = '\u2014';
                        if (strat.type === 'CONFIRMATION' && cond0.signal) {
                          const comp = t.companions?.[cond0.signal];
                          if (comp?.time && t.entry_time) {
                            try { offset = ((new Date(t.entry_time) - new Date(comp.time)) / 60000).toFixed(1) + 'm'; } catch {}
                          }
                        } else if (strat.conditions?.length >= 2) {
                          const cA = t.companions?.[strat.conditions[0]?.signal];
                          const cB = t.companions?.[strat.conditions[1]?.signal];
                          if (cA?.time && cB?.time) {
                            try { offset = ((new Date(cB.time) - new Date(cA.time)) / 60000).toFixed(1) + 'm'; } catch {}
                          }
                        }
                        const rColor = t.status === 'WIN' ? '#22c55e' : '#ef4444';
                        return (
                          <Box key={ti} sx={{ display: 'grid', gridTemplateColumns: '90px 140px 140px 70px 70px 65px 100px 55px',
                            gap: 0.5, px: 1, py: 0.3, borderBottom: `1px solid ${alpha('#fff', 0.02)}` }}>
                            <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.52rem', fontFamily: MONO }}>{(t.entry_time?.split('T')||[''])[0]}</Typography>
                            <Typography sx={{ color: '#22c55e', fontSize: '0.52rem', fontFamily: MONO }}>{fmtDt(t.entry_time)}</Typography>
                            <Typography sx={{ color: '#ef4444', fontSize: '0.52rem', fontFamily: MONO }}>{fmtDt(t.exit_time)}</Typography>
                            <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.52rem', fontFamily: MONO }}>{t.entry_price?.toLocaleString()}</Typography>
                            <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.52rem', fontFamily: MONO }}>{t.exit_price?.toLocaleString()}</Typography>
                            <Typography sx={{ color: rColor, fontSize: '0.52rem', fontWeight: 700, fontFamily: MONO }}>{pnl}</Typography>
                            <Typography sx={{ color: '#00d4ff', fontSize: '0.52rem', fontFamily: MONO }}>{offset}</Typography>
                            <Typography sx={{ color: rColor, fontSize: '0.52rem', fontWeight: 700, fontFamily: MONO }}>{t.status}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Generate Config button */}
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                      <Button size="small" startIcon={<CodeIcon sx={{ fontSize: 14 }} />}
                        onClick={() => {
                          const json = generateStrategyConfig(strat);
                          const name = `${strat.entry_signal || 'STRAT'} ${strat.type} ${tw.start}\u2013${tw.end}`;
                          setConfigModal({ open: true, json, stratName: name, copied: false });
                        }}
                        sx={{ fontSize: '0.62rem', fontFamily: MONO, color: '#a855f7', textTransform: 'none',
                          border: `1px solid ${alpha('#a855f7', 0.3)}`, borderRadius: 1, px: 1.5,
                          '&:hover': { bgcolor: alpha('#a855f7', 0.08) } }}>
                        Generate Config
                      </Button>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>}

          {/* Tab 1: Config Download */}
          {resultsTab === 1 && (
            <Stack spacing={1.5}>
              {(result.strategies || []).map((strat, idx) => {
                const tw = strat.time_window;
                const wrColor = strat.win_rate >= 90 ? '#22c55e' : strat.win_rate >= 80 ? '#f59e0b' : '#ef4444';
                const json = generateStrategyConfig(strat);
                const isCopied = copiedConfigIdx === idx;
                const entryName = strat.entry_signal || entrySignal || 'strat';
                const safeFilename = `config_${entryName.replace(/[^A-Z0-9]/gi, '_')}_${strat.type}_${(tw.start || '').replace(':', '')}_${(tw.end || '').replace(':', '')}.json`;

                const handleDownload = () => {
                  const blob = new Blob([json], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = safeFilename;
                  a.click(); URL.revokeObjectURL(url);
                };
                const handleCopy = () => {
                  navigator.clipboard.writeText(json);
                  setCopiedConfigIdx(idx);
                  setTimeout(() => setCopiedConfigIdx(null), 2000);
                };

                return (
                  <Paper key={idx} elevation={0}
                    sx={{ bgcolor: alpha('#fff', 0.02), border: `1px solid ${alpha(wrColor, 0.12)}`, borderRadius: 1.5, overflow: 'hidden' }}>
                    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha('#fff', 0.05)}` }}>
                      <Chip label={`#${strat.id}`} size="small" sx={{ height: 18, fontSize: '0.5rem', fontWeight: 800, fontFamily: MONO,
                        bgcolor: alpha(wrColor, 0.12), color: wrColor, '& .MuiChip-label': { px: 0.5 } }} />
                      <Chip label={strat.type} size="small" sx={{ height: 16, fontSize: '0.44rem', fontWeight: 700, fontFamily: MONO,
                        bgcolor: alpha('#a855f7', 0.1), color: '#a855f7', '& .MuiChip-label': { px: 0.5 } }} />
                      {strat.entry_label && (
                        <Chip label={strat.entry_label} size="small" sx={{ height: 16, fontSize: '0.44rem', fontWeight: 700, fontFamily: MONO,
                          bgcolor: alpha('#22c55e', 0.1), color: '#22c55e', '& .MuiChip-label': { px: 0.5 } }} />
                      )}
                      <Typography sx={{ color: wrColor, fontSize: '0.72rem', fontWeight: 800, fontFamily: MONO }}>{strat.win_rate}% WR</Typography>
                      <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.6rem', fontFamily: MONO }}>{strat.trade_count} trades</Typography>
                      <Chip label={`${tw.start}\u2013${tw.end}`} size="small" sx={{ height: 16, fontSize: '0.46rem', fontFamily: MONO,
                        bgcolor: alpha('#00d4ff', 0.08), color: '#00d4ff', '& .MuiChip-label': { px: 0.5 } }} />
                      <Box sx={{ flex: 1 }} />
                      <Button size="small"
                        startIcon={isCopied ? <CheckCircleIcon sx={{ fontSize: 12 }} /> : <CopyIcon sx={{ fontSize: 12 }} />}
                        onClick={handleCopy}
                        sx={{ fontSize: '0.58rem', fontFamily: MONO, textTransform: 'none', borderRadius: 1, px: 1.2,
                          color: isCopied ? '#22c55e' : alpha('#fff', 0.5),
                          border: `1px solid ${isCopied ? alpha('#22c55e', 0.4) : alpha('#fff', 0.12)}`,
                          '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
                        {isCopied ? 'Copied!' : 'Copy'}
                      </Button>
                      <Button size="small" startIcon={<FileDownloadIcon sx={{ fontSize: 12 }} />}
                        onClick={handleDownload}
                        sx={{ fontSize: '0.58rem', fontFamily: MONO, textTransform: 'none', borderRadius: 1, px: 1.2,
                          color: '#22c55e', border: `1px solid ${alpha('#22c55e', 0.3)}`,
                          '&:hover': { bgcolor: alpha('#22c55e', 0.06) } }}>
                        Download
                      </Button>
                    </Stack>
                    <Box component="pre" sx={{
                      m: 0, px: 2, py: 1.5, maxHeight: 220, overflowY: 'auto',
                      color: '#e2e8f0', fontSize: '0.62rem', fontFamily: MONO, lineHeight: 1.6, whiteSpace: 'pre',
                    }}>
                      {json}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {/* Generate Config modal */}
      <Dialog open={configModal.open} onClose={() => setConfigModal(m => ({ ...m, open: false, copied: false }))}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: '#0a0e17', border: `1px solid ${alpha('#a855f7', 0.3)}`, borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CodeIcon sx={{ color: '#a855f7', fontSize: 18 }} />
            <Typography sx={{ color: '#e2e8f0', fontFamily: MONO, fontSize: '0.82rem', fontWeight: 700 }}>
              Strategy Config &mdash; {configModal.stratName}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setConfigModal(m => ({ ...m, open: false, copied: false }))} sx={{ color: alpha('#fff', 0.3) }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.58rem', fontFamily: MONO, mb: 1.5 }}>
            Copy this config and paste it into the Monaco editor at{' '}
            <span style={{ color: alpha('#fff', 0.55) }}>officer/strategy-config</span>{' '}
            to deploy this strategy.
          </Typography>
          <Box component="pre" sx={{
            bgcolor: alpha('#000', 0.6), border: `1px solid ${alpha('#fff', 0.08)}`,
            borderRadius: 1.5, p: 2, m: 0, overflowX: 'auto', maxHeight: 420, overflowY: 'auto',
            color: '#e2e8f0', fontSize: '0.68rem', fontFamily: MONO, lineHeight: 1.65, whiteSpace: 'pre',
          }}>
            {configModal.json}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button size="small" onClick={() => setConfigModal(m => ({ ...m, open: false, copied: false }))}
            sx={{ fontSize: '0.65rem', fontFamily: MONO, color: alpha('#fff', 0.35), textTransform: 'none' }}>
            Close
          </Button>
          <Button size="small" variant="contained"
            startIcon={configModal.copied
              ? <CheckCircleIcon sx={{ fontSize: 14 }} />
              : <CopyIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              navigator.clipboard.writeText(configModal.json || '');
              setConfigModal(m => ({ ...m, copied: true }));
              setTimeout(() => setConfigModal(m => ({ ...m, copied: false })), 2000);
            }}
            sx={{ fontSize: '0.65rem', fontFamily: MONO, textTransform: 'none', borderRadius: 1, px: 2,
              bgcolor: configModal.copied ? '#22c55e' : '#a855f7',
              '&:hover': { bgcolor: configModal.copied ? '#16a34a' : '#9333ea' } }}>
            {configModal.copied ? 'Copied!' : 'Copy Config'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Picker modal */}
      <SignalPickerModal open={pickerOpen} onClose={closePicker} onSelect={handlePick}
        roleLabel={roleLabel} roleColor={roleColor} filterMode={isFilterMode}
        companionMode={isCompanionMode} defaults={{ timeframe, asset, session }} />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ fontFamily: MONO, fontSize: '0.7rem' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
